import React from "react";
import { sheetPayload } from "../utils/consolidationPresentation.js";

const DraftContext = React.createContext(null);
const PREFIX = "grain-consolidation-drafts-v1:";
function read(key) { try { return JSON.parse(sessionStorage.getItem(key) || "{}"); } catch { return {}; } }

export const isDispatchDraft = (draft, day) => draft.kind === "sheet" && draft.baseline?.dispatch_date === day
  && !draft.baseline?.manual_order_id && !draft.baseline?.source_key?.startsWith("manual:");

export function ConsolidationDraftProvider({ scope, children }) {
  const key = PREFIX + scope;
  const [drafts, setDrafts] = React.useState(() => read(key));
  const [replacements, setReplacements] = React.useState({});
  const current = React.useRef(drafts);
  current.current = drafts;
  const put = React.useCallback((id, value) => {
    const previous = current.current;
    if (JSON.stringify(previous[id]) === JSON.stringify(value)) return;
    const next = { ...previous };
    if (value) next[id] = value; else delete next[id];
    current.current = next;
    try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* Memory still preserves this tab's drafts. */ }
    setDrafts(next);
  }, [key]);
  const readDraft = React.useCallback(id => current.current[id], []);
  const forDay = React.useCallback(day => Object.values(current.current).filter(d => isDispatchDraft(d, day))
    .map(({ baseline, values }) => ({ baseline, values })), []);
  const replace = React.useCallback((rows, retired = []) => {
    for (const id of retired) {
      if (current.current[id]) put(`archived:${id}:${Date.now()}`, { ...current.current[id], kind: "archived-sheet" });
      put(id, null);
    }
    for (const row of rows) {
      const id = row.source_key || row.id;
      if (row.id) put(id, null);
      else {
        const quantities = reviewedQuantities(row);
        put(id, { kind: "sheet", baseline: row.review_baseline || row, row, quantities, values: sheetPayload(row, quantities) });
      }
    }
    setReplacements(previous => ({ ...previous, ...Object.fromEntries(rows.map(row => [row.source_key || row.id, row])) }));
  }, [put]);
  const value = React.useMemo(() => ({ readDraft, put, forDay, replace, replacements, drafts }), [readDraft, put, forDay, replace, replacements, drafts]);
  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

export default function useConsolidationDrafts() { return React.useContext(DraftContext); }

export const reviewedQuantities = row => Object.fromEntries(row.visits.flatMap(v => v.orders).map(o => [o.id,
  Object.fromEntries(["loaded_tons", "delivered_tons", "hourly_rate_override", "loading_tonne_rate_override", "fixed_rate_override"]
    .filter(k => !["loading_tonne_rate_override", "fixed_rate_override"].includes(k) || Object.prototype.hasOwnProperty.call(o, k))
    .map(k => [k, o[k] ?? ""]))]));

// Compare the loaded baseline, never the user's edited fields, with the new read.
// Les versions PM changent avec les relevés qui portent le PM et leurs journées : comme les autres jetons, elles se rafraîchissent.
export function compatibleConsolidationDraft(before, next) {
  if (!before || before.id !== next.id || before.status !== next.status || before.cycle_id !== next.cycle_id) return false;
  const quantitiesOnly = before.version === next.version && (!before.quantity_guard_versions
    || JSON.stringify(before.quantity_guard_versions) === JSON.stringify(next.quantity_guard_versions));
  const business = row => ({
    source_key: row.source_key, employee_id: row.employee_id, truck_id: row.truck_id,
    service_start: row.service_start, service_end: row.service_end, truck_departure: row.truck_departure, lunch_start: row.lunch_start, lunch_end: row.lunch_end,
    no_lunch: row.no_lunch, breaks: row.breaks, notes: row.notes,
    quantity_mode: row.quantity_mode,
    execution_decisions: row.execution_decisions, quantity_drafts: quantitiesOnly ? undefined : row.quantity_drafts,
    inspection_minutes: row.inspection_minutes, planned_service_start: row.planned_service_start, planned_service_end: row.planned_service_end,
    visits: row.visits.map(v => ({ kind: v.kind, label: v.label, planned_sequence: v.planned_sequence,
      source_step_ids: v.source_step_ids, planned_arrival: v.planned_arrival, planned_departure: v.planned_departure,
      arrival: v.arrival, departure: v.departure, status: v.status, actual_sequence: v.actual_sequence, notes: v.notes,
      orders: v.orders.map(o => ({ id: o.id, planned_tons: o.planned_tons, loaded_tons: quantitiesOnly ? undefined : o.loaded_tons,
        delivered_tons: quantitiesOnly ? undefined : o.delivered_tons, execution_excluded: o.execution_excluded, order_execution_status: o.order_execution_status, execution_correction_version: o.execution_correction_version, execution_confirmation: o.execution_confirmation, hourly_rate_override: quantitiesOnly ? undefined : o.hourly_rate_override, loading_tonne_rate_override: quantitiesOnly ? undefined : o.loading_tonne_rate_override, fixed_rate_override: quantitiesOnly ? undefined : o.fixed_rate_override, source_mill: o.source_mill, delivery_ids: o.delivery_ids })) })),
  });
  return JSON.stringify(business(before)) === JSON.stringify(business(next));
}

// Sauvegarde non confirmée (coupure) : le relevé relu porte-t-il les valeurs envoyées ? Alors elle a abouti.
// Heures du service, départ, dîner, pauses, notes, arrêts (ordre et heures), choix d'exécution, tonnages et tarifs saisis.
export function unconfirmedSaveApplied(sent, next) {
  if (!sent || !next?.id) return false;
  try {
    const read = sheetPayload(next);
    const instant = value => (value ? Date.parse(value) : null);
    const amount = value => (value == null || value === "" ? null : Number(value));
    const times = payload => [
      ...["service_start", "service_end", "truck_departure", "lunch_start", "lunch_end"].map(key => instant(payload[key])),
      ...payload.breaks.map(pause => [instant(pause.start), instant(pause.end), pause.paid]),
      ...payload.visits.map(visit => [visit.actual_sequence, instant(visit.arrival), instant(visit.departure), visit.notes])];
    const decisions = payload => payload.execution_decisions.map(decision => `${decision.order_id}:${decision.status}`).sort();
    const orders = new Map(next.visits.flatMap(visit => visit.orders).map(order => [order.id, order]));
    return sent.no_lunch === read.no_lunch && sent.notes === read.notes
      && JSON.stringify(times(sent)) === JSON.stringify(times(read))
      && JSON.stringify(decisions(sent)) === JSON.stringify(decisions(read))
      && sent.quantities.every(({ order_id, ...fields }) => orders.has(order_id)
        && Object.entries(fields).every(([key, value]) => amount(value) === amount(orders.get(order_id)[key])));
  } catch {
    return false;
  }
}

// Les données PM d'un arrêt viennent des journées liées, jamais de la saisie : elles suivent la nouvelle lecture.
const PM_VISIT_FIELDS = ["pm", "pm_source", "pm_delivery", "pm_contribution_state", "pm_review_required", "pm_review_reason"];
// « Facturer à » s'enregistre dans la commande (Data Manager), jamais dans la saisie : la relecture fait foi, brouillon compris.
const BILL_TO_FIELDS = ["coop", "coop_valid", "coop_party", "coop_locked"];
export const freshBillTo = (order, read) => (read ? { ...order, ...Object.fromEntries(BILL_TO_FIELDS.map(key => [key, read[key]])) } : order);
export function refreshConsolidationTokens(row, next) {
  const read = new Map(next.visits.flatMap(visit => visit.orders).map(order => [order.id, order]));
  return { ...row, day_version: next.day_version, day_editable: next.day_editable,
    pm_linked_versions: next.pm_linked_versions,
    source_version: next.source_version, shared_versions: next.shared_versions, version: next.version,
    visits: row.visits.map((visit, index) => ({ ...visit, ...Object.fromEntries(PM_VISIT_FIELDS.map(key => [key, next.visits[index][key]])),
      id: next.visits[index].id, orders: visit.orders.map(order => freshBillTo(order, read.get(order.id))) })) };
}
